/*
 * Copyright (c) 2022 E-WIND - Thais Watanabe.
 * All rights reserved.
 */
import React, { useEffect, useState } from "react";
import { API, graphqlOperation } from "aws-amplify";
import { deleteTechnician } from "src/graphql/mutations";
import toast from "react-hot-toast";
import {
  UserPlusIcon,
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";

import { queryTechnicians } from "src/services/AmplifyQuery";

import AddTechnician from "src/components/modals/AddTechnician.react";
import RemoveModal from "src/components/modals/RemoveModal.react";

/**
 * The Technicians component
 *
 * @author Thais Watanabe
 */
const Technicians = () => {
  const [technicians, setTechnicians] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [selectedTechnician, setSelectedTechnician] = useState(null);

  /* useEffect */
  useEffect(() => {
    // Scroll to top
    window.scrollTo(0, 0);
  }, []);

  /* useEffect */
  useEffect(() => {
    fetchTechnicians();
  }, []);

  /**
   * fetchTechnicians
   */
  async function fetchTechnicians() {
    queryTechnicians(null, setTechnicians);
  }

  /**
   * openRemoveDialog
   * @param {*} photo
   */
  async function openRemoveDialog(technician) {
    setSelectedTechnician(technician);
    setIsRemoveModalOpen(true);
  }

  /**
   * closeRemoveDialog
   */
  async function closeRemoveDialog() {
    setSelectedTechnician(null);
    setIsRemoveModalOpen(false);
  }

  /**
   * removeTechnician
   */
  async function removeTechnician() {
    try {
      await API.graphql(
        graphqlOperation(deleteTechnician, {
          input: {
            id: selectedTechnician.id,
            _version: selectedTechnician._version,
          },
        })
      );

      const updatedTechnicians = technicians.filter(
        (technician) => technician.id !== selectedTechnician.id
      );
      setTechnicians(updatedTechnicians);
      closeRemoveDialog();
      toast.success("保存しました");
    } catch (err) {
      console.log(err);
      toast.error("エラーが発生しました");
    }
  }

  /**
   * showEdit
   * @param {*} technician
   */
  function showEdit(technician) {
    setSelectedTechnician(technician);
    setIsOpen(!isOpen);
  }

  return (
    <>
      <section aria-labelledby="billing_history_heading">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 md:flex md:items-center md:justify-between md:space-x-5 lg:max-w-7xl lg:px-8">
          <div className="flex items-center space-x-5">
            <div>
              <h2
                id="billing_history_heading"
                className="text-lg leading-6 font-medium text-gray-900"
              >
                作業者一覧
              </h2>
            </div>
          </div>
          <div className="mt-6 flex flex-col-reverse justify-stretch space-y-4 space-y-reverse sm:flex-row-reverse sm:justify-end sm:space-x-reverse sm:space-y-0 sm:space-x-3 md:mt-0 md:flex-row md:space-x-3">
            <button
              type="button"
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-teal-500"
              onClick={() => setIsOpen(!isOpen)}
            >
              {/* <!-- Heroicon name: user-add  --> */}
              <UserPlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
              新規作業者登録
            </button>
          </div>
        </div>
        <div className="mt-6 flex flex-col overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  名前
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  組織
                </th>
                <th></th>
                <th></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {technicians.length < 1 && (
                <tr>
                  <td
                    colSpan="4"
                    className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                  >
                    <div className="loader"></div>
                  </td>
                </tr>
              )}
              {technicians
                .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
                .map((technician, i) => (
                  <tr key={i}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {technician.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {technician.company}
                    </td>
                    <td>
                      <button
                        onClick={() => showEdit(technician)}
                        className="rounded-full flex items-center text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-teal-500"
                      >
                        {/* <!-- Heroicon name: pencil-alt --> */}
                        <PencilSquareIcon className="h-5 w-5" />
                      </button>
                    </td>
                    <td>
                      <button
                        onClick={() => openRemoveDialog(technician)}
                        className="rounded-full flex items-center text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-teal-500"
                      >
                        {/* <!-- Heroicon name: trash --> */}
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </section>

      <AddTechnician
        setIsOpen={setIsOpen}
        isOpen={isOpen}
        technicians={technicians}
        setTechnicians={setTechnicians}
        selectedTechnician={selectedTechnician}
        setSelectedTechnician={setSelectedTechnician}
      />

      <RemoveModal
        open={isRemoveModalOpen}
        setOpen={setIsRemoveModalOpen}
        remove={removeTechnician}
      />
    </>
  );
};

export default Technicians;
